// The F&B menu and food orders (F-FNB-01, -02, -03; F-POS-01; spec/13 §6.1, spec/06 §8, §10).
//
// The `food` module owns two things on the wire:
//
//   the menu, organisation-scoped with per-venue overrides (spec/13 §6.1) —
//     GET/POST/PATCH …/product-categories        the headings a menu is grouped under (F-FNB-02)
//     GET/POST/PATCH …/products                   products with variants and modifiers (F-FNB-01)
//     PATCH …/products/{id}/availability          the "sold out" switch, organisation-wide (F-FNB-03)
//     GET  …/venues/{v}/menu                       one venue's effective menu — its price and
//                                                   availability applied — read by the console, the
//                                                   kiosk and the kitchen
//     PATCH …/venues/{v}/menu                       a venue's price and availability overrides
//     PATCH …/venues/{v}/menu/products/{id}/availability  the venue's "sold out" switch (F-FNB-03),
//                                                   `products:availability` — not `products:manage`
//
//   food orders, venue-scoped (spec/06 §8) —
//     POST …/venues/{v}/food-orders                place an order of menu items (F-POS-01)
//     GET  …/venues/{v}/food-orders                the order list
//     GET  …/venues/{v}/food-orders/{id}            one order, with its lines and kitchen routing
//     POST …/venues/{v}/food-orders/{id}/cancel     cancel a placed order before it is settled
//
// **Settlement is a seam.** An order records what is owed and routes to the kitchen; how it is paid —
// wallet, on-account, cash — and the `sales_fnb` posting to the ledger are the `commerce`/`payments`
// build and plug in later, exactly as the session spine left funding as a seam ([ADR-0020](../../adr/0020-food-and-kitchen-foundation-scope.md)).
//
// Money is an integer minor unit plus an ISO code everywhere; a product's tax is decided once and
// stored on each line as GST components that add back to the price (spec/06 §10.1). The C# component
// names are prefixed (`Menu…`, `Food…`) so they do not collide with the station's own types.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { FileIdSchema, FoodOrderIdSchema, FoodOrderLineIdSchema, FoodOrderLineModifierIdSchema, MenuCategoryIdSchema, PlayerIdSchema, ProductIdSchema, ProductModifierGroupIdSchema, ProductModifierIdSchema, ProductVariantIdSchema, SaleIdSchema, SessionIdSchema, StationIdSchema, TaxCategoryIdSchema, } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { MoneySchema, NonNegativeMoneySchema, PositiveMoneySchema } from "../primitives/money.js";
import { BasisPointsSchema, IdempotencyKeySchema, IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema, VenueScopeParamsSchema } from "../primitives/scope.js";
import { TimeWindowSchema } from "../primitives/time-window.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { StationPassPurchaseUpiSchema, StationPassTenderSchema, StationScanToPaySchema } from "./passes.js";
import { UploadedImageSchema } from "./venues.js";
import { ORDERS_CREATE, ORDERS_READ, ORDERS_VOID, PRODUCTS_AVAILABILITY, PRODUCTS_MANAGE, PRODUCTS_READ, } from "../permissions/index.js";
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5). The station provides its ' +
    'own organisation, venue and station from the token; the order is placed for the player in session on it.';
// ---------------------------------------------------------------------------------------------
// Shared vocabulary (spec/13 §6.1, spec/06 §8.1, §10.1)
// ---------------------------------------------------------------------------------------------
/**
 * A product's diet marker (spec/13 §6.1). Shown with an icon and an aria-label — never colour alone.
 * `none` is "not marked", so the field is always present rather than nullable (a nullable named enum
 * does not round-trip through the C# generator, which takes only string enums).
 */
export const FoodDietMarkerSchema = z
    .enum(['none', 'veg', 'egg', 'non_veg', 'vegan'])
    .openapi('FoodDietMarker', { description: "A product's diet marker; 'none' is unmarked (spec/13 §6.1)." });
/** Where a product is made (spec/13 §6.1). `none` needs no kitchen ticket (a packaged snack, a can). */
export const FoodPreparationAreaSchema = z
    .enum(['kitchen', 'bar', 'none'])
    .openapi('FoodPreparationArea', { description: 'The station that prepares a product (spec/13 §6.1).' });
/** The channel an order was placed from (spec/06 §8.2). The counter and, later, the kiosk and app. */
export const FoodOrderChannelSchema = z
    .enum(['counter', 'kiosk', 'app', 'web', 'api'])
    .openapi('FoodOrderChannel', { description: 'Where a food order was placed (spec/06 §8.2).' });
/** Where a food order is in its own lifecycle (spec/06 §8.2, the F&B foundation's subset). */
export const FoodOrderStatusSchema = z
    .enum(['placed', 'cancelled'])
    .openapi('FoodOrderStatus', { description: 'A food order’s state (spec/06 §8.2).' });
/**
 * How far a kiosk order has got, for the PC's order tracker (spec/12 §7.6, F-KIO-11). This tracks the
 * order's **kitchen** progress, not its `FoodOrderStatus`: the kitchen ticket's `new` reads as `placed`,
 * then `preparing → ready → delivered`. An order with several tickets is the least-advanced of them.
 */
export const FoodOrderTrackerStatusSchema = z
    .enum(['placed', 'preparing', 'ready', 'delivered'])
    .openapi('FoodOrderTrackerStatus', { description: 'Where a kiosk order is in the kitchen (spec/12 §7.6).' });
/**
 * The live state of a food order placed from a PC, for that PC's order tracker (spec/12 §7.6, F-KIO-11).
 * The server pushes it to the station as the order's kitchen tickets move; the kiosk shows Placed → …
 * → Delivered. It carries no money — the order was priced when it was placed. `placedAt` lets the station
 * ignore an order from a session that has since ended.
 */
export const FoodOrderTrackerSchema = z
    .object({
    /**
     * The order the tracker follows: a kiosk food order is a sale (`sale_…`, F-KIO-10); an older one placed
     * before that change is a food order (`fo_…`).
     */
    orderId: z.union([SaleIdSchema, FoodOrderIdSchema]),
    /**
     * The order's human number for the venue's business day (F-FNB-04), shown "#0421" — null until it is
     * allocated, just after the order is placed, and for a food order placed before order numbers.
     */
    orderNumber: z.int().min(1).nullable(),
    status: FoodOrderTrackerStatusSchema,
    /** A short human list of what was ordered, e.g. "Masala Maggi ×2, Cold coffee". */
    summary: z.string(),
    placedAt: IsoDateTimeSchema,
})
    .openapi('FoodOrderTracker', { description: 'A kiosk order’s live tracker (spec/12 §7.6, F-KIO-11).' });
/**
 * The `station.food.order` command payload (spec/09 §6.2, F-KIO-11): the station's active kiosk-order
 * tracker, or `null` to clear it (every ticket cancelled). The server sends it whenever the order's
 * kitchen tickets change; the station forwards it to the kiosk's Food screen.
 */
export const StationFoodOrderCommandSchema = z
    .object({ order: FoodOrderTrackerSchema.nullable() })
    .openapi('StationFoodOrderCommand', { description: 'The `station.food.order` command payload (spec/09 §6.2).' });
/** Whether an order has been settled (spec/06 §8.2). Settlement is a seam; the foundation records it. */
export const FoodOrderSettlementStatusSchema = z
    .enum(['unsettled', 'settled'])
    .openapi('FoodOrderSettlementStatus', {
    description: 'Whether a food order has been paid; settlement is the commerce/payments build (ADR-0020).',
});
/** One GST component stored on a line (spec/06 §10.1): CGST + SGST intra-state, IGST inter-state. */
export const FoodOrderTaxComponentSchema = z
    .object({
    component: z.enum(['CGST', 'SGST', 'IGST']),
    rateBps: BasisPointsSchema,
    amount: MoneySchema,
})
    .openapi('FoodOrderTaxComponent', { description: 'A line’s GST component (spec/06 §10.1).' });
const NAME = z.string().min(1).max(120);
const DESCRIPTION = z.string().min(1).max(2000).nullable();
/**
 * When a product or a category may be sold (F-FNB-02, spec/13 §6.1): recurring windows in the **venue's**
 * timezone ("breakfast only", "after 18:00"). Empty means any time. Outside every window the item stays listed
 * but disabled on every surface, and the server refuses a cart that names it.
 */
const AvailabilityWindowsSchema = z.array(TimeWindowSchema).max(14);
// ---------------------------------------------------------------------------------------------
// The menu — what a venue offers (spec/13 §6.1)
// ---------------------------------------------------------------------------------------------
/** One modifier a player may add to a product, with its price (spec/13 §6.1). */
export const MenuModifierSchema = z
    .object({
    id: ProductModifierIdSchema,
    name: NAME,
    price: NonNegativeMoneySchema,
    available: z.boolean(),
    sortOrder: z.int().min(0),
})
    .openapi('MenuModifier');
/** A group of modifiers, required or optional, with a min/max number of choices (spec/13 §6.1). */
export const MenuModifierGroupSchema = z
    .object({
    id: ProductModifierGroupIdSchema,
    name: NAME,
    required: z.boolean(),
    minChoices: z.int().min(0).max(50),
    maxChoices: z.int().min(1).max(50),
    sortOrder: z.int().min(0),
    modifiers: z.array(MenuModifierSchema),
})
    .openapi('MenuModifierGroup');
/** A product's variant — a size or option that sets the unit price (spec/13 §6.1). */
export const MenuProductVariantSchema = z
    .object({
    id: ProductVariantIdSchema,
    name: NAME,
    price: NonNegativeMoneySchema,
    sortOrder: z.int().min(0),
})
    .openapi('MenuProductVariant');
/**
 * A product on the menu (spec/13 §6.1). `price` and `available` are the effective values for the
 * scope read: at organisation scope they are the product's own; at a venue they are the venue's
 * override where it has one.
 */
/**
 * Whether a product is food & drink or a retail item (F-POS-11, spec/13 §4). Retail reuses the
 * `product` table; `retail` products carry a barcode and are sold on the Sell "Retail" tab, `fnb`
 * ones on "Food & drinks". `fnb` by default, so an existing product is unchanged.
 */
export const ProductItemCategorySchema = z
    .enum(['fnb', 'retail'])
    .openapi('ProductItemCategory', { description: 'Whether a product is food & drink or retail (F-POS-11).' });
export const MenuProductSchema = z
    .object({
    id: ProductIdSchema,
    categoryId: MenuCategoryIdSchema,
    name: NAME,
    description: DESCRIPTION,
    price: NonNegativeMoneySchema,
    salePrice: NonNegativeMoneySchema.nullable(),
    taxRateBps: BasisPointsSchema,
    /**
     * The tax category it is taxed under (F-PAY-11). When set, `taxRateBps` is the category's rate, kept in step
     * when the category's rate changes, and the tax invoice prints the category's HSN/SAC code. Null: the
     * product's own rate and no code.
     */
    taxCategoryId: TaxCategoryIdSchema.nullable(),
    dietMarker: FoodDietMarkerSchema,
    allergens: z.array(z.string().min(1).max(60)),
    preparationArea: FoodPreparationAreaSchema,
    preparationSeconds: z.int().min(0).max(86_400).nullable(),
    /** Food & drink or retail (F-POS-11). Retail products carry a barcode. */
    itemCategory: ProductItemCategorySchema,
    /** The retail barcode a scanner reads at Sell; null for food & drink or an unbarcoded item (F-POS-11). */
    barcode: z.string().min(1).max(64).nullable(),
    /** The "sold out" switch (F-FNB-03): at a venue, that venue's own switch where it has flipped it. */
    available: z.boolean(),
    /**
     * Only on a venue's menu (2026-10-07, F-FNB-10): true when the venue's sold-out switch was turned off **by stock**
     * — none of the product's recipes can be made from what is on hand (`fnb.autoUnavailableOnZeroStock`) — rather
     * than by a person; stock turns it back on when stock arrives. Absent on the organisation's list.
     */
    soldOutForStock: z.boolean().optional(),
    visible: z.boolean(),
    membersOnly: z.boolean(),
    /** Where it may be sold (F-FNB-01 rule 4): a product excluded from a channel is not sellable there at all. */
    channels: z.array(FoodOrderChannelSchema),
    /** Whether a sold unit may be returned for a refund (F-FNB-01). True unless the manager says otherwise. */
    returnable: z.boolean(),
    /** When it may be sold, in the venue's timezone (F-FNB-02); empty is any time. */
    availabilityWindows: AvailabilityWindowsSchema,
    /**
     * Whether it is inside its own and its category's windows **now**, at the venue, when read (F-FNB-02).
     * Always true on the organisation's list, which has no venue clock. Orderable = `available && inSchedule`.
     */
    inSchedule: z.boolean(),
    sortOrder: z.int().min(0),
    variants: z.array(MenuProductVariantSchema),
    modifierGroups: z.array(MenuModifierGroupSchema),
    /**
     * The product's picture (F-FNB-01): a `product_image` upload, shown on Sell's tiles and the kiosk's Food
     * screen, which the station caches for offline (ADR-0028). Null: a tinted tile with the product's initial.
     */
    image: UploadedImageSchema.nullable(),
})
    .openapi('MenuProduct');
/** A menu category — a heading products are grouped under (spec/13 §6.1). */
export const MenuCategorySchema = z
    .object({
    id: MenuCategoryIdSchema,
    name: NAME,
    description: DESCRIPTION,
    sortOrder: z.int().min(0),
    visible: z.boolean(),
    /** When the category's products may be sold, in the venue's timezone (F-FNB-02); empty is any time. */
    availabilityWindows: AvailabilityWindowsSchema,
    /** Whether the category is inside its windows now, at the venue, when read; always true on the organisation's list. */
    inSchedule: z.boolean(),
})
    .openapi('MenuCategory');
export const MenuCategoryListSchema = z
    .object({ categories: z.array(MenuCategorySchema) })
    .openapi('MenuCategoryList');
export const MenuProductListSchema = z.object({ products: z.array(MenuProductSchema) }).openapi('MenuProductList');
/** One venue's effective menu: its categories and the products in them, price and availability applied. */
export const VenueMenuSchema = z
    .object({
    categories: z.array(MenuCategorySchema),
    products: z.array(MenuProductSchema),
    /**
     * Only on the station's read (`getStationMenu`, F-KIO-11): whether the venue takes food orders from the kiosk
     * (`fnb.kioskOrdering`). When false the menu is empty and the kiosk has no Food tab. Absent elsewhere.
     */
    kioskOrdering: z.boolean().optional(),
})
    .openapi('VenueMenu', { description: 'A venue’s effective menu, with its overrides applied (spec/13 §6.1).' });
// ---------------------------------------------------------------------------------------------
// Menu writes
// ---------------------------------------------------------------------------------------------
export const CreateMenuCategorySchema = z
    .object({
    name: NAME,
    description: DESCRIPTION.optional(),
    sortOrder: z.int().min(0).optional(),
    visible: z.boolean().optional(),
    availabilityWindows: AvailabilityWindowsSchema.optional(),
})
    .openapi('CreateMenuCategoryRequest');
export const UpdateMenuCategorySchema = z
    .object({
    name: NAME.optional(),
    description: DESCRIPTION.optional(),
    sortOrder: z.int().min(0).optional(),
    visible: z.boolean().optional(),
    availabilityWindows: AvailabilityWindowsSchema.optional(),
})
    .openapi('UpdateMenuCategoryRequest');
/**
 * Variants, groups and modifiers are written as a whole set, but **keep their ids** (2026-10-07, ADR-0040 §13): send
 * the `id` one was read with to keep it (and the stock recipes keyed on it, spec/13 §6.4); with no `id`, one of the
 * same name the product already has keeps its id; anything else is new, and one no longer named is removed.
 */
const MenuProductVariantInputSchema = z
    .object({
    /** The variant's id as read, to keep it (and its stock recipe) through the edit; absent for a new one. */
    id: ProductVariantIdSchema.optional(),
    name: NAME,
    price: NonNegativeMoneySchema,
    sortOrder: z.int().min(0).optional(),
})
    .openapi('MenuProductVariantInput');
const MenuModifierInputSchema = z
    .object({
    /** The modifier's id as read, to keep it (and its stock recipe) through the edit; absent for a new one. */
    id: ProductModifierIdSchema.optional(),
    name: NAME,
    price: NonNegativeMoneySchema,
    available: z.boolean().optional(),
    sortOrder: z.int().min(0).optional(),
})
    .openapi('MenuModifierInput');
const MenuModifierGroupInputSchema = z
    .object({
    /** The group's id as read, to keep it through the edit; absent for a new one. */
    id: ProductModifierGroupIdSchema.optional(),
    name: NAME,
    required: z.boolean().optional(),
    minChoices: z.int().min(0).max(50).optional(),
    maxChoices: z.int().min(1).max(50).optional(),
    sortOrder: z.int().min(0).optional(),
    modifiers: z.array(MenuModifierInputSchema).max(50),
})
    .openapi('MenuModifierGroupInput');
/** The product fields a create and an update share. Variants and modifier groups are replaced wholesale. */
const MenuProductBodySchema = z.object({
    categoryId: MenuCategoryIdSchema,
    name: NAME,
    description: DESCRIPTION.optional(),
    price: NonNegativeMoneySchema,
    salePrice: NonNegativeMoneySchema.nullable().optional(),
    /** The product's own rate. Ignored when `taxCategoryId` is set: the category's rate applies. */
    taxRateBps: BasisPointsSchema,
    /** The tax category (F-PAY-11), or null to tax by `taxRateBps` alone. */
    taxCategoryId: TaxCategoryIdSchema.nullable().optional(),
    dietMarker: FoodDietMarkerSchema.optional(),
    allergens: z.array(z.string().min(1).max(60)).max(30).optional(),
    preparationArea: FoodPreparationAreaSchema.optional(),
    preparationSeconds: z.int().min(0).max(86_400).nullable().optional(),
    itemCategory: ProductItemCategorySchema.optional(),
    barcode: z.string().min(1).max(64).nullable().optional(),
    available: z.boolean().optional(),
    visible: z.boolean().optional(),
    membersOnly: z.boolean().optional(),
    channels: z.array(FoodOrderChannelSchema).optional(),
    returnable: z.boolean().optional(),
    availabilityWindows: AvailabilityWindowsSchema.optional(),
    sortOrder: z.int().min(0).optional(),
    variants: z.array(MenuProductVariantInputSchema).max(30).optional(),
    modifierGroups: z.array(MenuModifierGroupInputSchema).max(30).optional(),
    /** The product's picture: a completed `product_image` upload of this organisation, or null to remove it. */
    imageFileId: FileIdSchema.nullable().optional(),
});
export const CreateMenuProductSchema = MenuProductBodySchema.openapi('CreateMenuProductRequest');
export const UpdateMenuProductSchema = MenuProductBodySchema.partial().openapi('UpdateMenuProductRequest');
export const SetProductAvailabilitySchema = z
    .object({ available: z.boolean() })
    .openapi('SetProductAvailabilityRequest');
/** One venue override of a product's price and/or availability (spec/13 §6.1). Null clears an override. */
export const MenuVenueOverrideSchema = z
    .object({
    productId: ProductIdSchema,
    available: z.boolean().nullish(),
    price: NonNegativeMoneySchema.nullish(),
})
    .openapi('MenuVenueOverride');
export const PutVenueMenuOverridesSchema = z
    .object({ overrides: z.array(MenuVenueOverrideSchema).max(500) })
    .openapi('PutVenueMenuOverridesRequest');
// ---------------------------------------------------------------------------------------------
// Food orders (spec/06 §8)
// ---------------------------------------------------------------------------------------------
/** One modifier chosen on an order line, priced when the order was placed (spec/06 §8.1). */
export const FoodOrderLineModifierSchema = z
    .object({
    id: FoodOrderLineModifierIdSchema,
    modifierId: ProductModifierIdSchema.nullable(),
    name: NAME,
    price: NonNegativeMoneySchema,
})
    .openapi('FoodOrderLineModifier');
/** One line of a food order, with its computed value and tax (spec/06 §8.1). */
export const FoodOrderLineSchema = z
    .object({
    id: FoodOrderLineIdSchema,
    productId: ProductIdSchema,
    variantId: ProductVariantIdSchema.nullable(),
    name: NAME,
    quantity: z.int().min(1).max(999),
    unitPrice: NonNegativeMoneySchema,
    modifiers: z.array(FoodOrderLineModifierSchema),
    taxRateBps: BasisPointsSchema,
    taxable: MoneySchema,
    tax: MoneySchema,
    total: MoneySchema,
    taxComponents: z.array(FoodOrderTaxComponentSchema),
    preparationArea: FoodPreparationAreaSchema,
})
    .openapi('FoodOrderLine');
/** A food order (spec/06 §8): its lines, its totals, its kitchen routing and its settlement seam. */
export const FoodOrderSchema = z
    .object({
    id: FoodOrderIdSchema,
    venueId: z.string(),
    channel: FoodOrderChannelSchema,
    status: FoodOrderStatusSchema,
    settlementStatus: FoodOrderSettlementStatusSchema,
    sessionId: SessionIdSchema.nullable(),
    stationId: StationIdSchema.nullable(),
    playerId: PlayerIdSchema.nullable(),
    note: z.string().min(1).max(2000).nullable(),
    lines: z.array(FoodOrderLineSchema),
    subtotal: MoneySchema,
    tax: MoneySchema,
    total: MoneySchema,
    routedToKitchen: z.boolean(),
    placedAt: IsoDateTimeSchema,
    cancelledAt: IsoDateTimeSchema.nullable(),
})
    .openapi('FoodOrder');
export const FoodOrderListSchema = z.object({ orders: z.array(FoodOrderSchema) }).openapi('FoodOrderList');
/** One line a caller wants to order: a product, an optional variant, chosen modifiers, a quantity. */
export const PlaceFoodOrderLineSchema = z
    .object({
    productId: ProductIdSchema,
    variantId: ProductVariantIdSchema.optional(),
    modifierIds: z.array(ProductModifierIdSchema).max(50).optional(),
    quantity: z.int().min(1).max(999),
})
    .openapi('PlaceFoodOrderLine');
/** Place an order against a session, a station or the counter (spec/06 §8.3). The server prices it. */
export const PlaceFoodOrderSchema = z
    .object({
    channel: FoodOrderChannelSchema.optional(),
    sessionId: SessionIdSchema.optional(),
    stationId: StationIdSchema.optional(),
    playerId: PlayerIdSchema.optional(),
    note: z.string().min(1).max(2000).optional(),
    lines: z.array(PlaceFoodOrderLineSchema).min(1).max(200),
})
    .openapi('PlaceFoodOrderRequest');
// ---------------------------------------------------------------------------------------------
// Routes: the menu (organisation-scoped)
// ---------------------------------------------------------------------------------------------
const CategoryParamsSchema = OrganizationScopeParamsSchema.extend({ categoryId: MenuCategoryIdSchema }).openapi('MenuCategoryParams');
const ProductParamsSchema = OrganizationScopeParamsSchema.extend({ productId: ProductIdSchema }).openapi('MenuProductParams');
export const listMenuCategoriesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/product-categories',
    operationId: 'listMenuCategories',
    tags: ['food'],
    summary: 'List the menu categories (F-FNB-02)',
    [PERMISSION_EXTENSION]: PRODUCTS_READ,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(MenuCategoryListSchema, 'The organisation’s menu categories, by sort order.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createMenuCategoryRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/product-categories',
    operationId: 'createMenuCategory',
    tags: ['food'],
    summary: 'Create a menu category (F-FNB-02)',
    description: 'Adds a category to the organisation’s menu. Requires `products:manage`. Audited.',
    [PERMISSION_EXTENSION]: PRODUCTS_MANAGE,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { 'application/json': { schema: CreateMenuCategorySchema } }, required: true },
    },
    responses: {
        201: jsonResponse(MenuCategorySchema, 'The category.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateMenuCategoryRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/product-categories/{categoryId}',
    operationId: 'updateMenuCategory',
    tags: ['food'],
    summary: 'Update a menu category (F-FNB-02)',
    description: 'Requires `products:manage`. Audited.',
    [PERMISSION_EXTENSION]: PRODUCTS_MANAGE,
    request: {
        params: CategoryParamsSchema,
        body: { content: { 'application/json': { schema: UpdateMenuCategorySchema } }, required: true },
    },
    responses: {
        200: jsonResponse(MenuCategorySchema, 'The category as saved.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such category in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listMenuProductsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/products',
    operationId: 'listMenuProducts',
    tags: ['food'],
    summary: 'List the menu products (F-FNB-01)',
    [PERMISSION_EXTENSION]: PRODUCTS_READ,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(MenuProductListSchema, 'The organisation’s products with variants and modifiers.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createMenuProductRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/products',
    operationId: 'createMenuProduct',
    tags: ['food'],
    summary: 'Create a menu product (F-FNB-01)',
    description: 'Adds a product with its variants and modifier groups. Money is integer minor units. ' +
        'Requires `products:manage`. Audited.',
    [PERMISSION_EXTENSION]: PRODUCTS_MANAGE,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { 'application/json': { schema: CreateMenuProductSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(MenuProductSchema, 'The product.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation or category.', 'not-found'),
        409: problemResponse('The category or a price is inconsistent with the request.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getMenuProductRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/products/{productId}',
    operationId: 'getMenuProduct',
    tags: ['food'],
    summary: 'Read one menu product (F-FNB-01)',
    [PERMISSION_EXTENSION]: PRODUCTS_READ,
    request: { params: ProductParamsSchema },
    responses: {
        200: jsonResponse(MenuProductSchema, 'The product.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such product in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateMenuProductRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/products/{productId}',
    operationId: 'updateMenuProduct',
    tags: ['food'],
    summary: 'Update a menu product (F-FNB-01)',
    description: 'Replaces the fields given, and — when `variants` or `modifierGroups` is present — the whole set, keeping the id ' +
        'of each variant, group and modifier sent with its `id` (or, with none, the one of the same name). ' +
        'Requires `products:manage`. Audited.',
    [PERMISSION_EXTENSION]: PRODUCTS_MANAGE,
    request: {
        params: ProductParamsSchema,
        body: { content: { 'application/json': { schema: UpdateMenuProductSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(MenuProductSchema, 'The product as saved.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such product or category in this organisation.', 'not-found'),
        409: problemResponse('The category or a price is inconsistent with the request.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const setProductAvailabilityRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/products/{productId}/availability',
    operationId: 'setProductAvailability',
    tags: ['food'],
    summary: 'Turn a product on or off (F-FNB-03)',
    description: 'The "sold out" switch: sets availability without editing the product, organisation-wide. ' +
        'Requires `products:availability`. Audited.',
    [PERMISSION_EXTENSION]: PRODUCTS_AVAILABILITY,
    request: {
        params: ProductParamsSchema,
        body: { content: { 'application/json': { schema: SetProductAvailabilitySchema } }, required: true },
    },
    responses: {
        200: jsonResponse(MenuProductSchema, 'The product as saved.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such product in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Routes: the venue menu and its overrides (venue-scoped)
// ---------------------------------------------------------------------------------------------
export const getVenueMenuRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/menu',
    operationId: 'getVenueMenu',
    tags: ['food'],
    summary: 'Read a venue’s effective menu (F-FNB-01, F-FNB-02)',
    description: 'The menu the venue offers, with its price and availability overrides applied. Requires `products:read`.',
    [PERMISSION_EXTENSION]: PRODUCTS_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(VenueMenuSchema, 'The venue’s effective menu.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const putVenueMenuOverridesRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/menu',
    operationId: 'putVenueMenuOverrides',
    tags: ['food'],
    summary: 'Set a venue’s menu overrides (F-FNB-01, F-FNB-03)',
    description: 'Sets, per product, the venue’s price and/or availability override; null clears one. ' +
        'Requires `products:manage`. Audited.',
    [PERMISSION_EXTENSION]: PRODUCTS_MANAGE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { 'application/json': { schema: PutVenueMenuOverridesSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(VenueMenuSchema, 'The venue’s effective menu, after the overrides.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue or product in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const VenueProductParamsSchema = VenueScopeParamsSchema.extend({ productId: ProductIdSchema }).openapi('VenueMenuProductParams');
export const setVenueProductAvailabilityRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/menu/products/{productId}/availability',
    operationId: 'setVenueProductAvailability',
    tags: ['food'],
    summary: 'Mark a product sold out, or back on, at one venue (F-FNB-03)',
    description: 'The one-tap "sold out" switch: writes the venue’s availability override without editing the product, so it is ' +
        'per venue and reversible — the organisation’s other venues keep selling it. An unavailable product stays listed ' +
        'and disabled everywhere, and a cart naming it is refused. Requires `products:availability` (not ' +
        '`products:manage`). Audited.',
    [PERMISSION_EXTENSION]: PRODUCTS_AVAILABILITY,
    request: {
        params: VenueProductParamsSchema,
        body: { content: { 'application/json': { schema: SetProductAvailabilitySchema } }, required: true },
    },
    responses: {
        200: jsonResponse(MenuProductSchema, 'The product as the venue now offers it.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue or product in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Routes: food orders (venue-scoped)
// ---------------------------------------------------------------------------------------------
const FoodOrderParamsSchema = VenueScopeParamsSchema.extend({ orderId: FoodOrderIdSchema }).openapi('FoodOrderParams');
export const placeFoodOrderRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/food-orders',
    operationId: 'placeFoodOrder',
    tags: ['food'],
    summary: 'Place a food order (F-POS-01)',
    description: 'Prices the order from the venue’s effective menu, records what is owed, and routes food to the ' +
        'kitchen (spec/06 §8.2). Does not take payment — settlement is the commerce/payments build (ADR-0020). ' +
        'Requires `orders:create`. Audited.',
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { 'application/json': { schema: PlaceFoodOrderSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(FoodOrderSchema, 'The placed order, with its priced lines and totals.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue, or a product or modifier is not on this venue’s menu.', 'not-found'),
        409: problemResponse('A product or modifier is sold out or unavailable at this venue.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/station/food-orders  — the player orders food and drink from the PC (F-KIO-11, F-KIO-10, F-POS-01)
// ---------------------------------------------------------------------------------------------
//
// The station-authenticated, self-service twin of the counter's Sell (F-POS-01): the player orders from the
// kiosk's Food screen (F-KIO-11), and the order is an ordinary `commerce` sale with `channel: kiosk` — priced
// from the venue's effective menu, with the station, session and player on it — so it is in the console's
// Orders ("Awaiting payment" until paid), settles to the ledger like a counter sale, and reaches the kitchen
// once, from the sale (`commerce.sale.placed`, F-FNB-04). The station supplies its own venue and station from
// the device token; the session and player are its own active session's (both absent with none). It is paid
// the four ways a kiosk pass is (F-KIO-14, `StationPassTender`):
//
//   desk         the default: nothing now — the order waits in Orders and staff take payment at the counter.
//   wallet       the player's wallet pays now (`wallet.enabled`, a registered player).
//   on_account   "Add to my tab": charged to the player's receivable now (`credit.enabled`, within their limit).
//   upi          a payment intent for the exact total; the kiosk shows its QR. If it lapses unpaid the order
//                stays in Orders to be paid at the desk (the food is already being made).
//
// A discount code the kiosk checked (F-POS-02) is applied to the sale before it is paid, with a use reserved —
// the order costs the discounted total. The station learns what became of an unpaid order (paid at the desk or
// by the UPI capture, or cancelled by staff) from the `station.food.checkout` command.
/** Where a kiosk food order's payment is (F-KIO-10). The kitchen's progress is the separate tracker. */
export const StationFoodCheckoutStatusSchema = z
    .enum(['awaiting_desk', 'awaiting_upi', 'paid', 'cancelled'])
    .openapi('StationFoodCheckoutStatus', { description: 'Where a kiosk food order’s payment is (F-KIO-10).' });
/**
 * A food order placed from the kiosk, as its sale stands (F-KIO-10, F-KIO-11): what it costs, how it is being
 * paid and whether it is. `id` is the sale in the console's Orders. Every amount is the server's.
 */
export const StationFoodCheckoutSchema = z
    .object({
    /** The kiosk order in the console's Orders. */
    id: SaleIdSchema,
    status: StationFoodCheckoutStatusSchema,
    /** How it was chosen to be paid — or, once paid, how it was (a counter tender reads as `desk`). */
    tender: StationPassTenderSchema,
    /** The lines as priced, before any discount (GST inclusive at a registered venue). */
    gross: MoneySchema,
    /** What discount codes and promotions took off; zero for most orders. */
    discount: MoneySchema,
    /** The code applied to the sale, as entered; `null` when none. */
    discountCode: z.string().nullable(),
    /** What the order costs: `gross − discount`. */
    total: MoneySchema,
    /** A short human list of what was ordered, e.g. "Masala Maggi ×2, Cold coffee". */
    summary: z.string(),
    /** The QR to show while it is `awaiting_upi`; `null` otherwise. */
    upi: StationPassPurchaseUpiSchema.nullable(),
    /** The venue's own UPI QR while a "Scan & pay" order is `awaiting_desk` (F-PAY-02); absent otherwise. */
    scanToPay: StationScanToPaySchema.optional(),
    /** What the wallet has paid towards it — the wallet part of a split tender (F-KIO-14). */
    walletPaid: MoneySchema,
    /** What the order still owes (the sale's outstanding, F-KIO-14); zero once paid. */
    outstanding: MoneySchema,
    placedAt: IsoDateTimeSchema,
    paidAt: IsoDateTimeSchema.nullable(),
})
    .openapi('StationFoodCheckout', { description: 'A food order placed from the kiosk, as its sale stands (F-KIO-10).' });
/**
 * The `station.food.checkout` command payload (spec/09 §6.2, F-KIO-10): an unpaid kiosk food order was paid (at
 * the desk or by the UPI capture) or cancelled by staff. The station forwards it to the kiosk's Food screen.
 */
export const StationFoodCheckoutCommandSchema = z
    .object({ checkout: StationFoodCheckoutSchema })
    .openapi('StationFoodCheckoutCommand', { description: 'The `station.food.checkout` command payload (spec/09 §6.2).' });
/**
 * Order food from the PC: the lines, an optional note, how it is paid (`desk` when absent), and optionally a
 * discount code (F-POS-02) the kiosk checked with `checkStationDiscountCode` — applied to the sale, a use
 * reserved. Venue, station, session and player are the token's; the price is the server's.
 */
export const PlaceStationFoodOrderSchema = z
    .object({
    note: z.string().min(1).max(2000).optional(),
    discountCode: z.string().min(4).max(24).optional(),
    tender: StationPassTenderSchema.optional(),
    /**
     * Split tender (F-KIO-14, spec/06 §8.3): this much from the player's wallet first, the rest by `tender` (`upi`, `desk`
     * or `on_account`). At most what the order owes; not with `tender: wallet`.
     */
    walletAmount: PositiveMoneySchema.optional(),
    /**
     * "Scan & pay (UPI)" (F-PAY-02, 2026-10-01): with `tender: desk` (or none), the player pays by scanning the venue's own
     * UPI QR at the PC; the order carries `scanToPay` while it waits on the desk. Ignored with any other tender, and when
     * the venue has no UPI ID.
     */
    scanToPay: z.boolean().optional(),
    lines: z.array(PlaceFoodOrderLineSchema).min(1).max(200),
})
    .openapi('PlaceStationFoodOrderRequest');
/**
 * The kiosk sends an `Idempotency-Key` (its attempt id) so a re-sent order answers the sale already made. It is
 * optional only so an older station that sends none still places a `desk` order.
 */
const StationFoodOrderHeadersSchema = z
    .object({ 'idempotency-key': IdempotencyKeySchema.optional() })
    .openapi('StationFoodOrderHeaders');
export const placeStationFoodOrderRoute = createRoute({
    method: 'post',
    path: '/v1/station/food-orders',
    operationId: 'placeStationFoodOrder',
    tags: ['food', 'stations'],
    summary: 'Order food from the PC (F-KIO-11, F-KIO-10)',
    description: `${STATION_AUTHENTICATION} Places the order as a kiosk sale (\`channel: kiosk\`) priced from the venue's ` +
        'effective menu — it shows in Orders and routes food to the kitchen (spec/06 §8.2) — applies the discount ' +
        'code if one is given, and pays it by the tender: `wallet` and `on_account` pay now, `upi` opens a payment ' +
        'intent and returns its QR, `desk` (the default) leaves it for the counter. Attributed to the station’s ' +
        'active session and player when it has one; `wallet` and `on_account` need a registered player. With an ' +
        '`Idempotency-Key`, a retry answers the order already placed. Audited.',
    request: {
        headers: StationFoodOrderHeadersSchema,
        body: { content: { 'application/json': { schema: PlaceStationFoodOrderSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(StationFoodCheckoutSchema, 'The placed order: paid, or waiting for the desk or the UPI payment.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('A product or modifier is not on this venue’s menu, or no such discount code.', 'not-found', 'discount-code-unknown'),
        409: problemResponse('A product is unavailable, the tender cannot pay for the order, or the discount code does not apply.', 'conflict', 'food-order-guest', 'wallet-disabled', 'wallet-insufficient-funds', 'credit-disabled', 'credit-limit-exceeded', 'online-payments-unavailable', 'discount-code-inactive', 'discount-code-expired', 'discount-code-minimum', 'discount-limit-reached', 'tender-exceeds-outstanding'),
        422: problemResponse('The same key was already used for a different order.', 'idempotency-key-reused', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/station/menu  — the station reads its venue's effective menu (F-KIO-11, F-FNB)
// ---------------------------------------------------------------------------------------------
//
// The kiosk shows the venue's Food menu from the PC, so the station pulls the same effective menu the
// console and the counter see (`getVenueMenu`), device-authenticated, and hands it to the kiosk over the
// WebView2 bridge. It is display data — an order placed from the kiosk is re-priced server-side from this
// same menu (`placeStationFoodOrder`), so it carries no signature (unlike the game library's launch
// recipes, spec/10 §7.1). The station provides its own venue from the device token.
export const getStationMenuRoute = createRoute({
    method: 'get',
    path: '/v1/station/menu',
    operationId: 'getStationMenu',
    tags: ['food', 'stations'],
    summary: 'The station reads its venue’s menu (F-KIO-11)',
    description: `${STATION_AUTHENTICATION} Returns the station's venue's effective F&B menu — categories and products with ` +
        'their per-venue prices and availability applied (spec/13 §6.1) — for the kiosk to show the Food screen: only ' +
        'visible products sold on the `kiosk` channel, in visible categories, and none at all (with `kioskOrdering: false`) ' +
        'when the venue has turned kiosk ordering off (`fnb.kioskOrdering`). Unavailable products stay, marked, so the ' +
        'kiosk shows them sold out. Display data, re-priced server-side at order time; not signed. The station pulls it ' +
        'on connect and again on `station.menu.update`.',
    responses: {
        200: jsonResponse(VenueMenuSchema, 'The venue’s effective menu.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listFoodOrdersRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/food-orders',
    operationId: 'listFoodOrders',
    tags: ['food'],
    summary: 'List a venue’s food orders (F-POS-15)',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(FoodOrderListSchema, 'The venue’s food orders, newest first.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getFoodOrderRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/food-orders/{orderId}',
    operationId: 'getFoodOrder',
    tags: ['food'],
    summary: 'Read one food order (F-POS-15)',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: { params: FoodOrderParamsSchema },
    responses: {
        200: jsonResponse(FoodOrderSchema, 'The order, with its lines and kitchen routing.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such order at this venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const cancelFoodOrderRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/food-orders/{orderId}/cancel',
    operationId: 'cancelFoodOrder',
    tags: ['food'],
    summary: 'Cancel a placed food order (F-POS-15)',
    description: 'Cancels an order that has not been settled, and cancels its kitchen tickets. ' +
        'Requires `orders:void`. Audited.',
    [PERMISSION_EXTENSION]: ORDERS_VOID,
    request: { params: FoodOrderParamsSchema },
    responses: {
        200: jsonResponse(FoodOrderSchema, 'The cancelled order.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such order at this venue.', 'not-found'),
        409: problemResponse('The order is already settled or already cancelled.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=food.js.map